/**
 * ============================================================================
 * Real Emissions Hook
 * ============================================================================
 * 
 * Hook pour gérer les émissions avec connexion à la base de données via tRPC.
 * Remplace useDemoEmissions pour le vrai Calculator.
 * 
 * Fonctionnalités:
 * - Ajouter/mettre à jour des émissions
 * - Supprimer des émissions
 * - Calculer les métriques par catégorie
 * - Gérer le total des émissions
 * 
 * ============================================================================
 */

import { trpc } from '@/lib/trpc';
import { useMemo } from 'react';

export interface RealEmission {
  id: number;
  fe_key: string;
  category: string;
  activity_value: number;
  factor_value: number;
  tco2e: number;
  description?: string;
  created_at: string;
}

export interface CategoryMetrics {
  total: number;
  count: number;
  completedCount: number;
  totalFactors: number;
  progress: number;
  benchmark: number;
  vsBenchmark: number;
}

export function useRealEmissions(periodId: string | null) {
  const utils = trpc.useUtils();
  
  // Fetch emissions for period
  const { data: emissionsData = [] } = trpc.calculator.listByPeriod.useQuery(
    { periodId: periodId || '' },
    { enabled: !!periodId }
  );

  // Add emission mutation
  const addMutation = trpc.calculator.createEntry.useMutation({
    onSuccess: () => {
      utils.calculator.listByPeriod.invalidate({ periodId: periodId || '' });
    },
  });

  // Update emission mutation
  const updateMutation = trpc.calculator.updateEntry.useMutation({
    onSuccess: () => {
      utils.calculator.listByPeriod.invalidate({ periodId: periodId || '' });
    },
  });

  // Delete emission mutation
  const deleteMutation = trpc.calculator.deleteEntry.useMutation({
    onSuccess: () => {
      utils.calculator.listByPeriod.invalidate({ periodId: periodId || '' });
    },
  });

  // Add or update emission
  const addEmission = async (emission: {
    fe_key: string;
    category: string;
    activity_value: number;
    factor_value: number;
    unit?: string;
    description?: string;
    periodId: string;
  }) => {
    // Check if emission already exists for this FE in this period
    const existing = emissions.find(
      (e: any) => e.fe_key === emission.fe_key && e.period_id === emission.periodId
    );

    // Calculate tCO2e
    const tco2e = emission.activity_value * emission.factor_value;

    if (existing) {
      // Update existing emission
      await updateMutation.mutateAsync({
        entryId: existing.id,
        activityValue: emission.activity_value,
        tco2e, // Recalculated value
        notes: emission.description,
      });
    } else {
      // Add new emission
      await addMutation.mutateAsync({
        periodId: emission.periodId,
        feKey: emission.fe_key,
        activityValue: emission.activity_value,
        unit: emission.unit || 'unit',
        tco2e,
        notes: emission.description,
      });
    }
  };

  // Remove emission
  const removeEmission = async (id: string) => {
    await deleteMutation.mutateAsync({ entryId: id });
  };

  const emissions = useMemo(() => {
    // Category is now provided by the backend via JOIN with fe_master
    return emissionsData || [];
  }, [emissionsData]);

  // Get emissions by category
  const getEmissionsByCategory = (category: string) => {
    return emissions.filter((e: any) => e.category === category);
  };

  // Calculate category metrics
  const getCategoryMetrics = (category: string, totalFactors: number): CategoryMetrics => {
    const categoryEmissions = getEmissionsByCategory(category);
    const total = categoryEmissions.reduce((sum: number, e: any) => sum + (e.tco2e || 0), 0);
    const count = categoryEmissions.length;
    const completedCount = categoryEmissions.filter((e: any) => e.activity_value > 0).length;
    const progress = totalFactors > 0 ? (completedCount / totalFactors) * 100 : 0;

    // Simulate benchmark (would come from API in production)
    const benchmark = total * (0.9 + Math.random() * 0.2);
    const vsBenchmark = benchmark > 0 ? ((total - benchmark) / benchmark) * 100 : 0;

    return {
      total,
      count,
      completedCount,
      totalFactors,
      progress,
      benchmark,
      vsBenchmark,
    };
  };

  // Get total emissions
  const totalEmissions = useMemo(() => {
    return emissions.reduce((sum: number, e: any) => sum + (e.tco2e || 0), 0);
  }, [emissions]);

  return {
    emissions,
    addEmission,
    removeEmission,
    getEmissionsByCategory,
    getCategoryMetrics,
    totalEmissions,
    isLoading: false,
    isSubmitting: addMutation.isPending || updateMutation.isPending,
  };
}

